// Recibo do vale em PDF, feito no próprio aparelho (funciona sem sinal).
// Sai com o nome e o documento de quem paga, o valor por extenso e a
// assinatura, se houver. O PDF vai pelo WhatsApp (compartilhar) ou é baixado.
import { tracosDoCaminho, ALTURA, LARGURA } from './assinatura.js';
import { reaisPorExtenso } from './extenso.js';
import { cpf as formatarCpf, data as formatarData, dinheiro, documento as formatarDocumento, placa as formatarPlaca } from './formato.js';

const MESES = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const FORMAS = {
  dinheiro: 'em dinheiro',
  pix: 'por Pix',
  transferencia: 'por transferência bancária',
  cartao: 'no cartão',
  boleto: 'por boleto',
  outro: '',
};

export function dataPorExtenso(iso) {
  const [a, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} de ${MESES[m - 1]} de ${a}`;
}

function horaLocal(iso, fuso) {
  if (!iso) return '';
  const quando = new Date(iso);
  const dia = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, day: '2-digit', month: '2-digit', year: 'numeric' }).format(quando);
  const hora = new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit' }).format(quando);
  return `${dia} às ${hora}`;
}

// Texto do recibo (separado do desenho do PDF para dar para testar).
export function textoDoRecibo({ vale, empresa, colaborador, viagem = null, veiculo = null }) {
  const doc = empresa.documento ? ` (${empresa.tipo_pessoa === 'PF' ? 'CPF' : 'CNPJ'} ${formatarDocumento(empresa.documento)})` : '';
  const referente = vale.tipo === 'pagamento' ? 'pagamento' : 'vale (adiantamento de viagem)';
  const daViagem = viagem
    ? ` da viagem${veiculo ? ` do caminhão ${formatarPlaca(veiculo.placa)}` : ''} com saída em ${formatarData(viagem.data_saida)}`
    : '';
  const forma = FORMAS[vale.forma] ?? '';
  const paragrafos = [
    `Recebi de ${empresa.nome}${doc} a importância de ${dinheiro(vale.valor)} (${reaisPorExtenso(vale.valor)}), ` +
      `referente a ${referente}${daViagem}, paga em ${formatarData(vale.data)}${forma ? ` ${forma}` : ''}.`,
  ];
  if (vale.descricao) paragrafos.push(`Observação: ${vale.descricao}`);
  const local = [empresa.cidade, empresa.uf].filter(Boolean).join('/');
  return {
    titulo: vale.tipo === 'pagamento' ? 'RECIBO DE PAGAMENTO' : 'RECIBO DE VALE',
    valor: dinheiro(vale.valor),
    paragrafos,
    localData: `${local ? `${local}, ` : ''}${dataPorExtenso(vale.data)}.`,
    nome: colaborador?.nome ?? '',
    documento: colaborador?.cpf ? `CPF ${formatarCpf(colaborador.cpf)}` : '',
    assinado: vale.assinatura
      ? `Assinado ${vale.assinado_via === 'link' ? 'pelo link, no celular do recebedor,' : 'na tela do celular'} em ${horaLocal(vale.assinado_em, empresa.fuso_horario)}.`
      : 'Sem assinatura.',
    codigo: String(vale.id).slice(0, 8).toUpperCase(),
  };
}

export function nomeDoArquivo({ vale, colaborador }) {
  const pessoa = (colaborador?.apelido || colaborador?.nome || 'colaborador')
    .normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
  return `recibo-vale-${pessoa}-${vale.data}.pdf`;
}

// Monta o PDF e devolve um Blob.
export async function reciboEmPdf(dados) {
  const { jsPDF } = await import('jspdf');
  const t = textoDoRecibo(dados);
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const margem = 20;
  const largura = 210 - margem * 2;

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(18);
  pdf.text(t.titulo, margem, 30);
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(90);
  pdf.text(`Nº ${t.codigo}`, margem, 36);

  // Valor em destaque
  pdf.setTextColor(0);
  pdf.setDrawColor(0);
  pdf.setLineWidth(0.4);
  pdf.roundedRect(210 - margem - 58, 22, 58, 16, 2, 2);
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(15);
  pdf.text(t.valor, 210 - margem - 29, 32.5, { align: 'center' });

  pdf.setLineWidth(0.2);
  pdf.line(margem, 44, 210 - margem, 44);

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(12);
  let y = 56;
  for (const paragrafo of t.paragrafos) {
    const linhas = pdf.splitTextToSize(paragrafo, largura);
    pdf.text(linhas, margem, y, { lineHeightFactor: 1.5 });
    y += linhas.length * 12 * 0.3528 * 1.5 + 5;
  }
  y += 4;
  pdf.text(t.localData, margem, y);

  // Assinatura
  const linhaY = y + 42;
  const caixa = { x: 105 - 45, y: linhaY - 30, l: 90, a: 90 * (ALTURA / LARGURA) };
  if (dados.vale.assinatura) {
    pdf.setLineWidth(0.5);
    pdf.setLineCap('round');
    pdf.setLineJoin('round');
    const k = caixa.l / LARGURA;
    for (const traco of tracosDoCaminho(dados.vale.assinatura)) {
      const pontos = traco.length === 1 ? [traco[0], [traco[0][0] + 0.5, traco[0][1]]] : traco;
      for (let i = 1; i < pontos.length; i++) {
        pdf.line(caixa.x + pontos[i - 1][0] * k, caixa.y + pontos[i - 1][1] * k, caixa.x + pontos[i][0] * k, caixa.y + pontos[i][1] * k);
      }
    }
  }
  pdf.setLineWidth(0.3);
  pdf.line(105 - 55, linhaY, 105 + 55, linhaY);
  pdf.setFontSize(11);
  pdf.setFont('helvetica', 'bold');
  pdf.text(t.nome, 105, linhaY + 6, { align: 'center' });
  pdf.setFont('helvetica', 'normal');
  if (t.documento) pdf.text(t.documento, 105, linhaY + 11.5, { align: 'center' });
  pdf.setFontSize(9);
  pdf.setTextColor(90);
  pdf.text(t.assinado, 105, linhaY + (t.documento ? 17 : 11.5), { align: 'center' });

  pdf.setFontSize(8);
  pdf.text('Emitido pelo HSA Frota.', margem, 285);

  return pdf.output('blob');
}

// No celular, abre o compartilhar (WhatsApp…); no computador, baixa o arquivo.
export async function entregarArquivo(blob, nome, titulo) {
  const arquivo = typeof File === 'function' ? new File([blob], nome, { type: blob.type || 'application/pdf' }) : null;
  const celular = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  if (arquivo && celular && navigator.canShare?.({ files: [arquivo] })) {
    try {
      await navigator.share({ files: [arquivo], title: titulo });
      return 'compartilhado';
    } catch (erro) {
      if (erro?.name === 'AbortError') return 'cancelado';
      // sem permissão para compartilhar: baixa
    }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return 'baixado';
}
